// Temele vizuale: interfața și graficul dintr-o singură sursă.
//
// Interfața se colorează prin variabile CSS, iar Lightweight Charts vrea culori ca
// valori JavaScript. Ca să nu ajungem cu două palete care se depărtează una de alta,
// tema scrie variabilele pe `:root`, iar graficul *le citește de acolo* — o singură
// definiție per temă, nu una pentru CSS și alta pentru pânză.
//
// Culorile lumânărilor sunt separate de temă intenționat: cine ține la verde-roșu clasic
// vrea să le păstreze când schimbă restul interfeței. Tema dă doar valorile implicite.

import { bus } from './util.js';
import { settings } from './state.js';

const KEY_THEME = 'sx_theme';
//: Semnul ca tema a fost aleasa de om, nu scrisa de aplicatie la pornire.
//:
//: Pana acum, pornirea salva tema curenta chiar daca nimeni n-o ceruse, iar cine
//: intrase o data pe site ramanea legat de ea pe veci — schimbarea temei implicite
//: n-ar fi ajuns la el niciodata. Semnul acesta desparte alegerea de intamplare: fara
//: el, tema salvata nu conteaza si se ia cea implicita.
const KEY_ALEASA = 'sx_tema_aleasa';
const KEY_CANDLES = 'sx_candle_colors';

/**
 * Tema se citește înainte ca setările să apuce să vină de la server — altfel prima
 * randare ar fi în paleta implicită și abia apoi ar sări în cea aleasă. Cheile sunt
 * marcate ca locale în `state.js`, deci `localStorage` le are deja.
 */
function read(key, fallback) {
  if (settings.loaded) return settings.get(key, fallback);
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : JSON.parse(raw);
  } catch {
    return fallback;
  }
}

/**
 * Fiecare temă dă setul complet de tokeni. Nimic nu se moștenește între teme: o cheie
 * uitată ar rămâne cu valoarea temei precedente și ar apărea ca text invizibil abia
 * după ce utilizatorul comută în ordinea potrivită.
 */
export const THEMES = [
  {
    id: 'night',
    label: 'Ночь',
    hint: 'Тёмно-синяя палитра ScalpX',
    swatch: ['#0B0E16', '#131826', '#0F8BFD', '#16C784'],
    tokens: {
      bg: '#0B0E16', 'bg-1': '#0E1220', 'bg-2': '#131826', 'bg-3': '#1A2133',
      panel: '#131826',
      surface: 'rgba(255, 255, 255, .04)', 'surface-2': 'rgba(255, 255, 255, .07)',
      border: 'rgba(255, 255, 255, .06)', 'border-2': '#22293B',
      text: '#E8EEF7', 'text-2': '#DBE4F0', 'text-dim': '#9FAEC4',
      'text-mute': '#7D8AA3', 'text-faint': '#66748C',
      'text-strong': '#fff', 'switch-off': '#2a3446',
      'scroll-thumb': '#263248', 'scroll-thumb-hover': '#33405a',
      'dens-long': '#7FE0B4', 'dens-short': '#FF9FB0',
      overlay: 'rgba(11, 14, 22, .82)',
      accent: '#0F8BFD', 'accent-2': '#31C6F5',
      grad: 'linear-gradient(135deg, #0F8BFD 0%, #31C6F5 100%)',
      'grad-soft': 'linear-gradient(135deg, rgba(15,139,253,.20) 0%, rgba(49,198,245,.13) 100%)',
      'grad-bar': 'linear-gradient(90deg, rgba(15,139,253,.45), rgba(49,198,245,.28))',
      up: '#16C784', down: '#F6455F', warn: '#E6B325',
      'p-bg': '#0D0B14', 'p-container': '#161222', 'p-hover': '#221D31',
      'p-purple': '#9438D8', 'p-blue': '#31A8E0', 'p-pink': '#F61C5E',
      'p-border': 'rgba(148, 56, 216, .25)',
      'chart-bg': '#0d1421', 'chart-text': '#7d8799',
      'chart-grid': 'rgba(38, 50, 71, .45)', 'chart-border': '#1e2836',
      'chart-crosshair': '#4a5568',
      'level-line': '#969aa8', 'level-label': 'rgba(178, 182, 198, .9)',
    },
    candles: { up: '#26a69a', down: '#ef5350', upWick: '#26a69a', downWick: '#ef5350' },
  },

  {
    id: 'midnight',
    label: 'Полночь',
    hint: 'Чёрный фон — контрастнее на OLED',
    swatch: ['#000000', '#0C0E13', '#2E9BFF', '#16C784'],
    tokens: {
      bg: '#000000', 'bg-1': '#06070A', 'bg-2': '#0C0E13', 'bg-3': '#14171F',
      panel: '#0C0E13',
      surface: 'rgba(255, 255, 255, .05)', 'surface-2': 'rgba(255, 255, 255, .09)',
      border: 'rgba(255, 255, 255, .07)', 'border-2': '#23262E',
      text: '#F2F5FA', 'text-2': '#E2E7EF', 'text-dim': '#98A1B0',
      'text-mute': '#757E8C', 'text-faint': '#5C6470',
      'text-strong': '#fff', 'switch-off': '#262A33',
      'scroll-thumb': '#22262F', 'scroll-thumb-hover': '#333844',
      'dens-long': '#7FE0B4', 'dens-short': '#FF9FB0',
      overlay: 'rgba(0, 0, 0, .84)',
      accent: '#2E9BFF', 'accent-2': '#56D2FF',
      grad: 'linear-gradient(135deg, #2E9BFF 0%, #56D2FF 100%)',
      'grad-soft': 'linear-gradient(135deg, rgba(46,155,255,.22) 0%, rgba(86,210,255,.14) 100%)',
      'grad-bar': 'linear-gradient(90deg, rgba(46,155,255,.45), rgba(86,210,255,.28))',
      up: '#16C784', down: '#F6455F', warn: '#E6B325',
      'p-bg': '#050509', 'p-container': '#101018', 'p-hover': '#1B1B26',
      'p-purple': '#A855F7', 'p-blue': '#38BDF8', 'p-pink': '#F61C5E',
      'p-border': 'rgba(168, 85, 247, .25)',
      'chart-bg': '#000000', 'chart-text': '#7B8494',
      'chart-grid': 'rgba(255, 255, 255, .06)', 'chart-border': '#23262E',
      'chart-crosshair': '#525A67',
      'level-line': '#9AA0AE', 'level-label': 'rgba(186, 192, 208, .9)',
    },
    candles: { up: '#16C784', down: '#F6455F', upWick: '#16C784', downWick: '#F6455F' },
  },

  {
    id: 'graphite',
    label: 'Графит',
    hint: 'Нейтральный серый, фиолетовый акцент',
    swatch: ['#16181C', '#202429', '#8B5CF6', '#34D399'],
    tokens: {
      bg: '#16181C', 'bg-1': '#1A1D22', 'bg-2': '#202429', 'bg-3': '#282D34',
      panel: '#202429',
      surface: 'rgba(255, 255, 255, .05)', 'surface-2': 'rgba(255, 255, 255, .08)',
      border: 'rgba(255, 255, 255, .07)', 'border-2': '#333941',
      text: '#ECEEF2', 'text-2': '#DDE1E8', 'text-dim': '#A2A9B4',
      'text-mute': '#808896', 'text-faint': '#6A7280',
      'text-strong': '#fff', 'switch-off': '#3A414B',
      'scroll-thumb': '#343A44', 'scroll-thumb-hover': '#464D59',
      'dens-long': '#63D6A4', 'dens-short': '#FF93A6',
      overlay: 'rgba(22, 24, 28, .84)',
      accent: '#8B5CF6', 'accent-2': '#C084FC',
      grad: 'linear-gradient(135deg, #8B5CF6 0%, #C084FC 100%)',
      'grad-soft': 'linear-gradient(135deg, rgba(139,92,246,.22) 0%, rgba(192,132,252,.14) 100%)',
      'grad-bar': 'linear-gradient(90deg, rgba(139,92,246,.45), rgba(192,132,252,.28))',
      up: '#34D399', down: '#FB7185', warn: '#FBBF24',
      'p-bg': '#131118', 'p-container': '#1D1A26', 'p-hover': '#2A2536',
      'p-purple': '#8B5CF6', 'p-blue': '#38BDF8', 'p-pink': '#FB7185',
      'p-border': 'rgba(139, 92, 246, .25)',
      'chart-bg': '#16181C', 'chart-text': '#8A919C',
      'chart-grid': 'rgba(255, 255, 255, .05)', 'chart-border': '#333941',
      'chart-crosshair': '#5A616C',
      'level-line': '#9DA3AE', 'level-label': 'rgba(190, 195, 205, .9)',
    },
    candles: { up: '#34D399', down: '#FB7185', upWick: '#34D399', downWick: '#FB7185' },
  },

  {
    id: 'day',
    label: 'День',
    hint: 'Светлая — для работы при ярком свете',
    swatch: ['#EEF1F6', '#FFFFFF', '#0F76E0', '#0E9F6E'],
    tokens: {
      bg: '#EEF1F6', 'bg-1': '#F4F6FA', 'bg-2': '#FFFFFF', 'bg-3': '#E6EBF3',
      panel: '#FFFFFF',
      surface: 'rgba(15, 23, 42, .04)', 'surface-2': 'rgba(15, 23, 42, .08)',
      border: 'rgba(15, 23, 42, .10)', 'border-2': '#D6DEEA',
      text: '#0F1729', 'text-2': '#1E293B', 'text-dim': '#4A5A73',
      'text-mute': '#6B7C96', 'text-faint': '#8A99B0',
      'text-strong': '#0B1220', 'switch-off': '#C7D0DE',
      'scroll-thumb': '#C2CCDB', 'scroll-thumb-hover': '#A7B3C6',
      'dens-long': '#0B8F63', 'dens-short': '#C41F45',
      overlay: 'rgba(255, 255, 255, .86)',
      accent: '#0F76E0', 'accent-2': '#1FA6D8',
      grad: 'linear-gradient(135deg, #0F76E0 0%, #1FA6D8 100%)',
      'grad-soft': 'linear-gradient(135deg, rgba(15,118,224,.14) 0%, rgba(31,166,216,.09) 100%)',
      'grad-bar': 'linear-gradient(90deg, rgba(15,118,224,.35), rgba(31,166,216,.22))',
      up: '#0E9F6E', down: '#E11D48', warn: '#B4780C',
      'p-bg': '#F3F0FA', 'p-container': '#FFFFFF', 'p-hover': '#EDE7F8',
      'p-purple': '#7B2FC4', 'p-blue': '#2189C4', 'p-pink': '#D6134B',
      'p-border': 'rgba(123, 47, 196, .22)',
      'chart-bg': '#FFFFFF', 'chart-text': '#5A6678',
      'chart-grid': 'rgba(15, 23, 42, .08)', 'chart-border': '#D6DEEA',
      'chart-crosshair': '#94A3B8',
      'level-line': '#7A8598', 'level-label': 'rgba(71, 85, 105, .9)',
    },
    candles: { up: '#0E9F6E', down: '#E11D48', upWick: '#0E9F6E', downWick: '#E11D48' },
  },
];

//: Cu ce temă se deschide proiectul pentru cine n-a ales încă una. „Полночь" — negru
//: curat: pe ecranele OLED ale telefoanelor pixelul negru e stins, deci contrastul e
//: mai mare şi bateria ţine mai mult, iar un grafic se citeşte după contrast.
const DEFAULT_ID = 'midnight';

export function themeById(id) {
  return THEMES.find((t) => t.id === id) || THEMES[0];
}

export function currentThemeId() {
  if (!read(KEY_ALEASA, false)) return DEFAULT_ID;
  return read(KEY_THEME, DEFAULT_ID);
}

export function currentTheme() {
  return themeById(currentThemeId());
}

/** Culorile lumânărilor: implicitele temei, peste care stau alegerile utilizatorului. */
export function candleColors(themeId = currentThemeId()) {
  const base = themeById(themeId).candles;
  const custom = read(KEY_CANDLES, null);
  return custom ? { ...base, ...custom } : { ...base };
}

/** Utilizatorul a schimbat vreo culoare de lumânare față de tema curentă? */
export function candlesCustomized() {
  const custom = read(KEY_CANDLES, null);
  if (!custom) return false;
  const base = currentTheme().candles;
  return Object.keys(base).some((k) => custom[k] && custom[k].toLowerCase() !== base[k].toLowerCase());
}

export function setCandleColors(patch) {
  const next = { ...candleColors(), ...patch };
  settings.set(KEY_CANDLES, next);
  bus.emit('theme:change', { theme: currentThemeId(), candles: next });
  return next;
}

/** Înapoi la culorile temei — nu la cele ale temei implicite. */
export function resetCandleColors() {
  settings.set(KEY_CANDLES, null);
  bus.emit('theme:change', { theme: currentThemeId(), candles: candleColors() });
  return candleColors();
}

/** Scrie tokenii temei pe `:root`. Interfața se recolorează fără reîncărcare. */
export function applyTheme(id = currentThemeId(), { persist = true } = {}) {
  const theme = themeById(id);
  const root = document.documentElement;
  for (const [name, value] of Object.entries(theme.tokens)) {
    root.style.setProperty(`--${name}`, value);
  }
  root.dataset.theme = theme.id;
  // Barele browserului (bara de derulare, câmpurile native) urmează schema declarată.
  root.style.colorScheme = theme.id === 'day' ? 'light' : 'dark';
  if (persist) {
    settings.set(KEY_THEME, theme.id);
    settings.set(KEY_ALEASA, true);
  }
  bus.emit('theme:change', { theme: theme.id, candles: candleColors(theme.id) });
  return theme;
}

/** Paleta pentru Lightweight Charts, citită din tokenii aplicați. */
export function chartTheme() {
  const css = getComputedStyle(document.documentElement);
  const read = (name, fallback) => (css.getPropertyValue(`--${name}`) || fallback).trim();
  const candles = candleColors();
  return {
    bg: read('chart-bg', '#0d1421'),
    text: read('chart-text', '#7d8799'),
    grid: read('chart-grid', 'rgba(38,50,71,0.45)'),
    border: read('chart-border', '#1e2836'),
    crosshair: read('chart-crosshair', '#4a5568'),
    accent: read('accent', '#2f81f7'),
    up: candles.up,
    down: candles.down,
    upWick: candles.upWick,
    downWick: candles.downWick,
    levelLine: read('level-line', '#969aa8'),
    levelLabel: read('level-label', 'rgba(178,182,198,0.9)'),
  };
}

/**
 * Culoarea barelor de volum: corpul lumânării, transparent.
 * Acceptă `#rgb`, `#rrggbb` și `rgb()/rgba()`, fiindcă temele și alegerile
 * utilizatorului pot veni în oricare dintre ele.
 */
export function withAlpha(color, alpha) {
  const value = String(color).trim();
  if (value.startsWith('#')) {
    const hex = value.slice(1);
    const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex.slice(0, 6);
    const n = parseInt(full, 16);
    if (Number.isNaN(n)) return value;
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  }
  const nums = value.match(/-?\d+(\.\d+)?/g);
  if (nums && nums.length >= 3) return `rgba(${nums[0]}, ${nums[1]}, ${nums[2]}, ${alpha})`;
  return value;
}
